import { CommentOutlined, ShrinkOutlined, ArrowsAltOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Ask } from '../pages/Ask.js';

/** ⚠ ต้องตรงกับคีย์ที่ FAB ใช้จำตำแหน่ง ดู KookitLauncher.tsx */
const SIZE_KEY = 'yana.kookit.size';

type Size = 'full' | 'mini';

function readSize(): Size {
  try {
    return localStorage.getItem(SIZE_KEY) === 'mini' ? 'mini' : 'full';
  } catch {
    return 'full';
  }
}

/**
 * คู่คิด — หน้าต่างถาม-ตอบด้วย AI
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมเพิ่งกลับมามีปุ่มเปิด
 * ────────────────────────────────────────────────────────────────
 *
 * ระบบถาม-ตอบมีมาตั้งแต่แรกและทำงานได้ครบ (เครื่องมือ 12 ตัว + ด่านตรวจถ้อยคำ)
 * แต่ตอนที่ยุบเมนูเจ็ดอันเหลือหน้าเดียว มันถูกยกออกไปพร้อมหน้าอื่นและไม่มีทางกดเข้าถึง
 * กรณีเดียวกับหน้าผู้มีอำนาจแต่งตั้งที่เอากลับเข้ามาก่อนหน้านี้
 *
 * ────────────────────────────────────────────────────────────────
 *  ⚠ เนื้อในมาจาก pages/Ask.tsx ตัวเดียวกัน ห้ามคัดลอกมาวางซ้ำ
 * ────────────────────────────────────────────────────────────────
 *
 * หน้านั้นมีตรรกะที่ห้ามมีสองสำเนาเด็ดขาด — การเก็บบทสนทนาไว้ที่หน้าจอ (ไม่ใช่เซิร์ฟเวอร์)
 * การตัดประวัติเหลือสามรอบล่าสุด และการไม่เก็บคำตอบที่มาจากประโยคแม่แบบเข้าประวัติ
 * ทั้งสามข้อเป็นเรื่องความเป็นส่วนตัวของข้อมูลบุคคลและความถูกต้องของบริบท
 * ถ้ามีสองสำเนา วันที่แก้ข้อใดข้อหนึ่งจะมีที่หนึ่งที่ยังส่งข้อมูลแบบเดิมออกไป
 */
export function KookitModal({ onClose }: { onClose: () => void }) {
  const closeBtn = useRef<HTMLButtonElement | null>(null);

  /**
   * ขนาดกล่อง — เต็มจอ (เว้นขอบ 10px) หรือย่อเป็นแผงมุมขวาล่าง
   *
   * จำไว้ข้ามการเปิดปิด เพราะเป็นความชอบส่วนตัวที่ไม่เปลี่ยนบ่อย
   * คนที่ชอบเปิดคู่คิดค้างไว้ข้างผังจะเลือกย่อ แล้วไม่ต้องมากดย่อใหม่ทุกครั้ง
   */
  const [size, setSize] = useState<Size>(readSize);

  const toggleSize = useCallback(() => {
    setSize((v) => {
      const next: Size = v === 'full' ? 'mini' : 'full';
      try {
        localStorage.setItem(SIZE_KEY, next);
      } catch {
        /* จำข้ามรอบไม่ได้ก็ไม่เป็นไร รอบนี้ยังถูกต้อง */
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const returnTo = document.activeElement as HTMLElement | null;
    closeBtn.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      returnTo?.focus?.();
    };
  }, [onClose]);

  return (
    /*
     * ⚠ ตอนย่อ ฉากหลังต้องไม่กินการกดทั้งจอ
     *
     * .pmodal-back คลุมเต็มจอเสมอเพื่อรับการกดนอกกล่องให้ปิด ซึ่งถูกต้องตอนเต็มจอ
     * แต่ตอนย่อเป็นแผงมุมขวาล่าง ผู้ใช้ยังต้องกดผังที่อยู่ข้างหลังได้
     * จึงต้องปิด pointer-events ของฉากหลัง แล้วเปิดคืนเฉพาะตัวกล่อง (ดู .kkback.is-mini)
     */
    <div
      className={`pmodal-back kkback is-${size}`}
      onMouseDown={(e) => {
        if (size === 'full' && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`pmodal kkmodal is-${size}`}
        role="dialog"
        aria-label="KOOKIT"
        /* aria-modal เฉพาะตอนเต็มจอ — ตอนย่อมันไม่ได้กันการใช้งานส่วนอื่นของหน้า
           ประกาศว่าเป็น modal ทั้งที่ไม่ได้กัน จะทำให้โปรแกรมอ่านหน้าจอบอกผู้ใช้ผิด */
        aria-modal={size === 'full'}
      >
        <header className="pmodal-hd">
          <span className="simicon" aria-hidden="true"><CommentOutlined /></span>
          <b>KOOKIT</b>
          <button
            type="button"
            className="kk-size"
            onClick={toggleSize}
            aria-label={size === 'full' ? 'ย่อกล่องแชท' : 'ขยายกล่องแชท'}
            title={size === 'full' ? 'ย่อลงมุมขวาล่าง' : 'ขยายเต็มจอ'}
          >
            {size === 'full' ? <ShrinkOutlined /> : <ArrowsAltOutlined />}
          </button>
          <button type="button" onClick={onClose} aria-label="ปิดหน้าต่าง" ref={closeBtn}>✕</button>
        </header>

        <div className="kkbody">
          <Ask />
        </div>
      </div>
    </div>
  );
}
